/**
 * 客户端错误上报工具。
 * fire-and-forget，不阻塞页面，不抛出。
 */

let sessionId: string | null = null;

function getSessionId(): string {
  if (sessionId) return sessionId;
  try {
    const key = "__error_session_id";
    const existing = sessionStorage.getItem(key);
    if (existing) {
      sessionId = existing;
      return existing;
    }
    sessionId = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(key, sessionId);
  } catch {
    sessionId = "no-session";
  }
  return sessionId;
}

export interface ErrorReportPayload {
  message: string;
  digest?: string;
  kind: string;
  pageUrl: string;
  userAgent: string;
  stack?: string;
  componentStack?: string;
  occurredAt?: string;
}

function shouldSkipClientError(payload: ErrorReportPayload): boolean {
  const message = (payload.message || "").trim();
  if (message === "Script error.") return true;
  if (message === "unknown" && !payload.stack && !payload.componentStack) return true;

  try {
    const url = new URL(payload.pageUrl);
    if (url.hostname === "localhost" || url.hostname === "127.0.0.1" || url.hostname === "::1") {
      return true;
    }
  } catch {
    // Keep malformed URLs reportable; the server will truncate before writing.
  }

  return false;
}

/**
 * 上报客户端错误到服务端。fire-and-forget，绝不抛出。
 */
export function reportClientError(payload: ErrorReportPayload): void {
  try {
    if (shouldSkipClientError(payload)) return;

    const body = JSON.stringify({
      ...payload,
      sessionId: getSessionId(),
      occurredAt: payload.occurredAt || new Date().toISOString(),
    });

    if (navigator.sendBeacon) {
      navigator.sendBeacon("/api/error-report", body);
    } else {
      fetch("/api/error-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {
        // ignore
      });
    }
  } catch {
    // ignore — 绝不因为上报失败影响用户体验
  }
}
